import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, shareReplay, map } from 'rxjs';
import { environment } from '#environments';
import { HierarchyNode, SourceSystem } from '#models';

@Injectable({ providedIn: 'root' })
export class HierarchyService {
    private _http = inject(HttpClient);
    private _base = `${environment.apiBaseUrl}/v1/reference-data`;

    /** Cached ARES source system definition — fetched once. */
    private _aresDefinition$: Observable<SourceSystem> | null = null;

    /**
     * Returns the ARES source system definition (ordered hierarchy type rules).
     * Result is cached for the lifetime of the service.
     */
    getAresDefinition(): Observable<SourceSystem> {
        this._aresDefinition$ ??= this._http
            .get<SourceSystem[]>(`${this._base}/hierarchy/source-systems`)
            .pipe(
                map(systems => {
                    const ares = systems.find(s => s.sourceSystem === 'ARES');
                    if (!ares) throw new Error('ARES source system definition not found.');
                    return ares;
                }),
                shareReplay(1),
            );
        return this._aresDefinition$;
    }

    /**
     * Returns hierarchy nodes filtered by source system and optional type.
     * Used to populate root-level dropdowns (e.g. all Verticals).
     */
    getHierarchyNodes(sourceSystem: string, type?: string): Observable<HierarchyNode[]> {
        const params: Record<string, string> = { sourceSystem };
        if (type) params['type'] = type;
        return this._http.get<HierarchyNode[]>(`${this._base}/hierarchy`, { params });
    }

    /**
     * Returns direct children of the given hierarchy node.
     * Used for cascading dropdowns on scope assignment form.
     */
    getHierarchyChildren(parentId: number): Observable<HierarchyNode[]> {
        return this._http.get<HierarchyNode[]>(`${this._base}/hierarchy/children/${parentId}`);
    }

    /**
     * Returns scope-assignment dropdown options for a given hierarchy type.
     * The backend reads AllowedParents from the ARES definition and applies
     * the appropriate computed-column filters — the frontend passes its full
     * current selection context and owns no column-mapping knowledge.
     *
     * @param type          Hierarchy type to fetch (e.g. 'Vertical', 'Subvertical')
     * @param selectedScopes Currently selected parent scope values, e.g. { Vertical: 'Onshore' }
     */
    getScopeOptions(type: string, selectedScopes: Record<string, string> = {}): Observable<{ hierarchyId: number; name: string }[]> {
        let params = new HttpParams().set('type', type);
        for (const [key, value] of Object.entries(selectedScopes)) {
            if (value) params = params.set(key, value);
        }
        return this._http.get<{ hierarchyId: number; name: string }[]>(
            `${this._base}/hierarchy/scope-options`,
            { params },
        );
    }
}
